// The spoken turn: microphone in, transcript to the session as an ordinary
// talk, and the reply out through the speaker. Framework-free. It never
// touches the socket; the session owns that.
import { toastDurationMs } from './beats';
import type { ExperienceSession, SessionState, SessionLine } from './ExperienceSession';
import type { SpeechPorts } from '../../ports/SpeechPorts';
import { SPEAK_CHIP, STOP_CHIP, VOICE_COPY } from './hudView';
import type { VoicePhase } from './hudView';
import type { VoiceCaptureSession } from '../../ports/VoiceCapturePort';
import { VoiceCaptureError } from '../../ports/VoiceCapturePort';
import { SpeechToTextError } from '../../ports/SpeechToTextPort';
import type { BrowserAudioPlaybackSession } from '../../ports/BrowserAudioPlaybackPort';

export const MIC_PREPROMPT_ACK_KEY = 'aeonia:experience-companion:mic-preprompt-acknowledged';

export interface VoiceState {
  phase: VoicePhase;
  message: string | null;
  micPrompted: boolean;
  /** Exact line whose audio is being prepared or played, for synchronized text. */
  speakingLine?: SessionLine | null;
  /** True only after the browser has started playing this line's audio. */
  playbackStarted?: boolean;
  /** Decoded/HTML audio clip length, never the synthesis provider's estimate. */
  playbackDurationMs?: number | null;
  /** Playback ended or failed, so the line remains fully visible. */
  playbackComplete?: boolean;
}

export type PermissionAnswer = 'granted' | 'denied' | 'prompt' | 'unknown';

/** The part of a hold gate VoiceTurn needs (the opening animation's HoldGate fits). */
export interface PlaybackGate {
  readonly released: boolean;
  whenReleased(callback: () => void): void;
}

export interface VoiceTurnOptions {
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  queryPermission?: () => Promise<PermissionAnswer>;
  isSupported?: () => boolean;
  now?: () => number;
  setTimeout?: typeof setTimeout;
  clearTimeout?: typeof clearTimeout;
  recordingCapMs?: number;
  minRecordingMs?: number;
  characterId?: () => string | undefined;
  outputFormat?: string;
}

const RECORDING_CAP_MS = 30_000;
const MIN_RECORDING_MS = 250;
const OUTPUT_FORMAT = 'mp3_44100_128';

export class VoiceTurn {
  private current: VoiceState = { phase: 'ready', message: null, micPrompted: false, playbackStarted: false, playbackDurationMs: null, playbackComplete: false };
  private readonly listeners = new Set<(state: VoiceState) => void>();
  private readonly unsubscribe: () => void;
  private capture: VoiceCaptureSession | null = null;
  private capTimer: ReturnType<typeof setTimeout> | null = null;
  private playing: BrowserAudioPlaybackSession | null = null;
  private failureHoldTimer: ReturnType<typeof setTimeout> | null = null;
  private queue: SessionLine[] = [];
  private generation = 0;
  private turnCounter = 0;
  private speechEnabled = false;
  /** Microphone state to return to after speaking; speaking itself never waits on the microphone. */
  private micBlocked: 'unsupported' | 'denied' | null = null;
  private playbackGate: PlaybackGate | null = null;
  private lastLine: SessionState['line'] = null;
  private disposed = false;
  private readonly opts: Required<Pick<VoiceTurnOptions, 'recordingCapMs' | 'minRecordingMs' | 'outputFormat'>> & VoiceTurnOptions;

  constructor(
    private readonly session: ExperienceSession,
    private readonly ports: SpeechPorts,
    options: VoiceTurnOptions = {},
  ) {
    this.opts = { recordingCapMs: RECORDING_CAP_MS, minRecordingMs: MIN_RECORDING_MS, outputFormat: OUTPUT_FORMAT, ...options };
    this.lastLine = session.state.line;
    this.unsubscribe = session.subscribe((state) => this.onSession(state));
    if (options.isSupported && !options.isSupported()) {
      this.current = { ...this.current, phase: 'unsupported' };
      this.micBlocked = 'unsupported';
      return;
    }
    if (this.read(MIC_PREPROMPT_ACK_KEY) === 'true') this.current = { ...this.current, micPrompted: true };
    void this.checkPermission();
  }

  get state(): VoiceState { return this.current; }

  subscribe(listener: (state: VoiceState) => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /**
   * Marks speech as enabled: lines that arrive after this call are spoken.
   * Called by `press()`, and also by the Enter that starts the mission, so
   * that first user gesture counts as the first interaction too.
   */
  enableSpeech(): void {
    this.speechEnabled = true;
  }

  /**
   * Playback of every line waits until this gate is released; fetching the audio does
   * not. The page passes the gate its opening animation also uses, so the first
   * line's voice starts when its text card appears instead of under the animation.
   * A released gate stays open, so it can never hold speech back a second time.
   */
  setPlaybackGate(gate: PlaybackGate | null): void {
    this.playbackGate = gate;
  }

  private async playbackGateOpen(): Promise<void> {
    const gate = this.playbackGate;
    if (!gate || gate.released) return;
    await new Promise<void>((resolve) => gate.whenReleased(resolve));
  }

  /** The one entry point a chip calls. */
  press(): void {
    if (this.disposed || this.current.phase === 'unsupported') return;
    this.enableSpeech();
    switch (this.current.phase) {
      case 'listening':
        void this.stopAndSend();
        return;
      case 'transcribing':
      case 'thinking':
        return;
      case 'speaking':
        this.interrupt();
        void this.startRecording();
        return;
      default:
        if (!this.current.micPrompted) {
          this.write(MIC_PREPROMPT_ACK_KEY, 'true');
          this.patch({ micPrompted: true, message: VOICE_COPY.micPrePrompt });
          return;
        }
        void this.startRecording();
    }
  }

  cancel(): void {
    if (this.disposed) return;
    this.interrupt();
    if (this.capture) {
      const c = this.capture;
      this.capture = null;
      this.clearCap();
      void c.cancel().catch(() => undefined);
    }
    if (this.current.phase !== 'unsupported' && this.current.phase !== 'denied') {
      this.patch({ phase: 'ready', message: null });
    }
  }

  pageHidden(): void { this.cancel(); }

  dispose(): void {
    if (this.disposed) return;
    this.cancel();
    this.disposed = true;
    this.unsubscribe();
    this.listeners.clear();
  }

  // ---- recording ----

  private async startRecording(): Promise<void> {
    this.clearFailureHold();
    if (this.capture) {
      const stale = this.capture;
      this.capture = null;
      this.clearCap();
      void stale.cancel().catch(() => undefined);
    }
    const gen = ++this.generation;
    try {
      const session = await this.ports.capture.start();
      if (gen !== this.generation || this.disposed) { void session.cancel().catch(() => undefined); return; }
      this.capture = session;
      this.patch({ phase: 'listening', message: null });
      this.capTimer = (this.opts.setTimeout ?? setTimeout)(() => {
        this.capTimer = null;
        void this.stopAndSend();
      }, this.opts.recordingCapMs);
    } catch (error) {
      if (error instanceof VoiceCaptureError && error.code === 'permission-denied') {
        this.micBlocked = 'denied';
        this.patch({ phase: 'denied', message: null });
      } else if (error instanceof VoiceCaptureError && error.code === 'unsupported') {
        this.micBlocked = 'unsupported';
        this.patch({ phase: 'unsupported', message: null });
      } else {
        this.patch({ phase: 'failed', message: VOICE_COPY.captureFailed });
      }
    }
  }

  private async stopAndSend(): Promise<void> {
    const capture = this.capture;
    if (!capture) return;
    this.capture = null;
    this.clearCap();
    const gen = this.generation;
    let result;
    try {
      result = await capture.stop();
    } catch {
      this.patch({ phase: 'failed', message: VOICE_COPY.captureFailed });
      this.drainIfQueued();
      return;
    }
    if (gen !== this.generation || this.disposed) return;
    if (result.durationMs < this.opts.minRecordingMs || result.blob.size === 0) {
      this.patch({ phase: 'ready', message: VOICE_COPY.tooShort });
      this.drainIfQueued();
      return;
    }
    const creds = this.session.credentials();
    if (!creds) {
      this.patch({ phase: 'ready', message: VOICE_COPY.sttUnknown });
      this.drainIfQueued();
      return;
    }
    this.patch({ phase: 'transcribing', message: null });
    try {
      const transcript = await this.ports.speechToText.transcribe({
        audio: result.blob,
        token: creds.token,
        experienceId: creds.experienceId,
        sessionId: creds.roomInstanceId,
        turnId: `voice-turn-${++this.turnCounter}`,
        contentType: result.mimeType || result.blob.type,
        durationMs: result.durationMs,
        maxDurationSeconds: Math.round(this.opts.recordingCapMs / 1000),
      });
      if (gen !== this.generation || this.disposed) return;
      const sent = this.session.sendTalk(transcript.text);
      this.patch(sent ? { phase: 'thinking', message: null } : { phase: 'ready', message: VOICE_COPY.sttUnknown });
      this.drainIfQueued();
    } catch (error) {
      if (gen !== this.generation || this.disposed) return;
      this.patch({ phase: 'failed', message: transcriptionCopy(error) });
      this.drainIfQueued();
    }
  }

  // ---- speaking ----

  private onSession(state: SessionState): void {
    if (this.disposed) return;
    if (state.phase !== 'connected' && this.capture) {
      this.cancel();
      return;
    }
    const line = state.line;
    const isNew = line !== null && line !== this.lastLine && line.text.trim() !== '';
    this.lastLine = line;
    if (isNew && this.speechEnabled) {
      this.queue.push(line);
      if (this.isSpeakable(this.current.phase)) void this.speakNext();
    } else if (this.current.phase === 'thinking' && !state.turnInFlight) {
      if (this.queue.length > 0) void this.speakNext();
      else this.patch({ phase: 'ready', message: null });
    }
  }

  /** Lines still queue outside these phases; they drain once the recording or turn ends. */
  private isSpeakable(phase: VoicePhase): boolean {
    return phase === 'ready' || phase === 'thinking' || phase === 'unsupported' || phase === 'denied'
      || (phase === 'failed' && this.failureHoldTimer === null);
  }

  /** Called wherever recording or transcription ends, so a line queued during it can play. */
  private drainIfQueued(): void {
    if (this.queue.length > 0) void this.speakNext();
  }

  private async speakNext(): Promise<void> {
    if (this.failureHoldTimer !== null) return;
    const line = this.queue.shift();
    if (line === undefined) { if (this.current.phase === 'speaking') this.patch({ phase: this.micBlocked ?? 'ready', message: null, speakingLine: null }); return; }
    const text = line.text;
    const gen = this.generation;
    const creds = this.session.credentials();
    if (!creds) { this.queue = []; this.patch({ phase: this.micBlocked ?? 'ready' }); return; }
    // A failure message (e.g. "Could not play voice response.") must survive the next queued line
    // starting to speak, not be wiped the instant playback for it begins.
    this.patch(this.current.phase === 'failed'
      ? { phase: 'speaking', speakingLine: line, playbackStarted: false, playbackDurationMs: null, playbackComplete: false }
      : { phase: 'speaking', speakingLine: line, message: null, playbackStarted: false, playbackDurationMs: null, playbackComplete: false });
    try {
      const audio = await this.ports.textToSpeech.synthesize({
        text, token: creds.token,
        characterId:
          this.opts.characterId?.() ?? this.session.state.characterId ?? this.session.state.bindings?.characterId,
        outputFormat: this.opts.outputFormat,
      });
      if (gen !== this.generation || this.disposed) return;
      await this.playbackGateOpen();
      if (gen !== this.generation || this.disposed) return;
      const playing = await this.ports.playback.play(audio);
      if (gen !== this.generation || this.disposed) { playing.stop(); return; }
      this.playing = playing;
      playing.onEnded?.(() => {
        if (gen !== this.generation || this.disposed) return;
        this.playing = null;
        this.patch({ playbackStarted: false, playbackComplete: true });
        void this.speakNext();
      });
      this.patch({ playbackStarted: true, playbackDurationMs: validDuration(playing.durationMs), playbackComplete: false });
    } catch {
      if (gen !== this.generation || this.disposed) return;
      await this.playbackGateOpen();
      if (gen !== this.generation || this.disposed) return;
      this.playing = null;
      this.failureHoldTimer = (this.opts.setTimeout ?? setTimeout)(() => {
        this.failureHoldTimer = null;
        if (gen === this.generation && !this.disposed && this.queue.length > 0) void this.speakNext();
      }, toastDurationMs(line.text));
      this.patch({ phase: 'failed', message: VOICE_COPY.playbackFailed, speakingLine: line, playbackStarted: false, playbackComplete: true });
    }
  }

  /** Drops queued and playing speech and invalidates in-flight results. */
  private interrupt(): void {
    this.clearFailureHold();
    this.generation += 1;
    this.queue = [];
    const p = this.playing;
    this.playing = null;
    try { p?.stop(); } catch { /* an already stopped player is fine */ }
    if (this.current.playbackStarted) this.patch({ playbackStarted: false, playbackComplete: true });
  }

  private clearFailureHold(): void {
    if (this.failureHoldTimer !== null) (this.opts.clearTimeout ?? clearTimeout)(this.failureHoldTimer);
    this.failureHoldTimer = null;
  }

  // ---- permission and storage ----

  private async checkPermission(): Promise<void> {
    if (!this.opts.queryPermission) return;
    let answer: PermissionAnswer = 'unknown';
    try { answer = await this.opts.queryPermission(); } catch { /* unknown */ }
    if (this.disposed) return;
    if (answer === 'denied' && this.current.phase === 'ready') { this.micBlocked = 'denied'; this.patch({ phase: 'denied' }); }
    if (answer === 'granted') this.patch({ micPrompted: true });
  }

  private read(key: string): string | null {
    try { return this.opts.storage?.getItem(key) ?? null; } catch { return null; }
  }
  private write(key: string, value: string): void {
    try { this.opts.storage?.setItem(key, value); } catch { /* best effort */ }
  }

  // ---- plumbing ----

  private clearCap(): void {
    if (this.capTimer) { (this.opts.clearTimeout ?? clearTimeout)(this.capTimer); this.capTimer = null; }
  }

  private patch(changes: Partial<VoiceState>): void {
    this.current = { ...this.current, ...changes };
    const snapshot = this.current;
    for (const listener of [...this.listeners]) {
      try { listener(snapshot); } catch { /* one listener must not stop the others */ }
    }
  }
}

function validDuration(durationMs: number | undefined): number | null {
  return durationMs !== undefined && Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null;
}

function transcriptionCopy(error: unknown): string {
  if (error instanceof SpeechToTextError) {
    switch (error.code) {
      case 'auth': return VOICE_COPY.sttAuth;
      case 'provider-unavailable': return VOICE_COPY.sttUnavailable;
      case 'network': return VOICE_COPY.sttNetwork;
      case 'malformed-response': return VOICE_COPY.sttMalformed;
      default: return VOICE_COPY.sttUnknown;
    }
  }
  return VOICE_COPY.sttUnknown;
}

/** True for either label the mic chip can show, so a chip handler can route to `press()`. */
export function isVoiceChip(label: string): boolean {
  return label === SPEAK_CHIP || label === STOP_CHIP;
}

/**
 * Wires a VoiceTurn's `pageHidden()` to the page's visibility signals: the tab going into the
 * background (`visibilitychange`, checked against `document.hidden` since it also fires when the
 * tab returns) and the page unloading (`pagehide`, which fires with the tab already hidden).
 * Returns a remover so a component can clean up on unmount.
 */
export function bindPageHidden(voice: VoiceTurn, doc: Pick<Document, 'hidden' | 'addEventListener' | 'removeEventListener'> = document, win: Pick<Window, 'addEventListener' | 'removeEventListener'> = window): () => void {
  const onHidden = () => { if (doc.hidden) voice.pageHidden(); };
  doc.addEventListener('visibilitychange', onHidden);
  win.addEventListener('pagehide', onHidden);
  return () => {
    doc.removeEventListener('visibilitychange', onHidden);
    win.removeEventListener('pagehide', onHidden);
  };
}
